'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { FaSearch } from 'react-icons/fa';
import { fetchHeaderSuggestions, sendSearchClick, type Suggestion } from '@/services/searchService';

interface SearchInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
}

const SearchInput: React.FC<SearchInputProps> = ({ value, onChange, onSearch, onKeyDown, placeholder }) => {
  const router = useRouter();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // One highlighted row at a time. `armed` is true only when the keyboard
  // (arrow keys) chose it: Enter opens an ARMED row, otherwise Enter always
  // goes to the results page. Hovering a row highlights it but never arms it,
  // so a cursor resting over the dropdown cannot turn Enter into a click on a
  // suggestion the visitor never looked at (the mistaken opens of 2026-10-05).
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [armed, setArmed] = useState(false);
  const clearHighlight = () => { setSelectedIndex(-1); setArmed(false); };
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const searchTriggered = useRef(false);
  // Handle the latest v2 response carries; the click beacon joins back on it.
  const searchEventId = useRef<string | null>(null);

  // Fetch suggestions with debouncing
  useEffect(() => {
    // Reset search trigger when value changes so suggestions can show again
    searchTriggered.current = false;
    // A highlight never survives a keystroke: new suggestions are a new list.
    clearHighlight();

    // Only fetch suggestions if we have 2+ chars AND the user is actually focused on the box
    // This prevents suggestions from automatically popping up on page load (e.g. after a category redirect)
    if (value.length >= 2 && isFocused) {
      setIsLoading(true);

      // Clear previous timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new timer
      debounceTimer.current = setTimeout(async () => {
        try {
          const { suggestions: results, searchEventId: eventId } = await fetchHeaderSuggestions(value);
          // Only update suggestions if a full search hasn't been triggered in the meantime
          // AND we are still focused
          if (!searchTriggered.current && isFocused) {
            searchEventId.current = eventId;
            setSuggestions(results);
            setShowSuggestions(true);
            clearHighlight();
          }
        } catch (error) {
          console.error('Failed to fetch suggestions:', error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, 300); // 300ms debounce
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        setArmed(true);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        setArmed(true);
      } else if (e.key === 'Enter') {
        if (armed && selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex]);
          return;
        }
        // Not chosen by keyboard (nothing, or only hovered): Enter = results page.
        searchTriggered.current = true;
        setShowSuggestions(false);
        clearHighlight();
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        clearHighlight();
      }
    }

    onKeyDown(e);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    // Fire the click beacon BEFORE navigating (sendBeacon survives unload).
    // Funnel attribution: which suggestion, at which position, for which search.
    const position = suggestions.findIndex(s => s.content_id === suggestion.content_id);
    sendSearchClick(searchEventId.current, suggestion.content_id, position, 'dropdown');
    // Navigate to the report detail page
    router.push(`/report/${suggestion.slug}`);
    setShowSuggestions(false);
    clearHighlight();
  };

  const handleSearchInternal = () => {
    searchTriggered.current = true;
    setShowSuggestions(false);
    clearHighlight();
    onSearch();
  };

  return (
    <div className="row" ref={wrapperRef} style={{ position: 'relative' }}>
      <div className="search-option">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => {
            // Delay blur slightly to allow suggestion clicks to register
            setTimeout(() => setIsFocused(false), 200);
          }}
        />
        <span className="submitbtn">
          <button type="submit" onClick={handleSearchInternal}>
            <FaSearch size={23} color="#666666" />
          </button>
        </span>

        {/* Autocomplete Dropdown */}
        {showSuggestions && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000,
              marginTop: '4px',
            }}
          >
            {suggestions.length === 0 ? (
              <div style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>
                No reports found
              </div>
            ) : (
              suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={() => { setSelectedIndex(index); setArmed(false); }}
                  onMouseLeave={() => { if (!armed) setSelectedIndex(-1); }}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: selectedIndex === index ? '#23494E' : '#fff',
                    borderBottom: index < suggestions.length - 1 ? '1px solid #eee' : 'none',
                    transition: 'background-color 0.2s, color 0.2s',
                  }}
                >
                  <div style={{
                    fontSize: '14px',
                    color: selectedIndex === index ? '#fff' : '#1B8E86',
                    transition: 'color 0.2s',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ fontWeight: 600 }}>{suggestion.title}</span>
                    {suggestion.summary && (
                      <span> - {suggestion.summary}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchInput;
